import {
  useQuery as useReactQuery,
  useMutation as useReactMutation,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAxios } from '@/api/axios/axiosClient';
import { ApiResponse, ApiError } from '@/types/api';

/**
 * Custom hook for data fetching using React Query and our API client
 *
 * @param endpoint - API endpoint to fetch data from
 * @param queryKey - React Query key for caching
 * @param options - Additional React Query options
 * @returns React Query result
 */
export function useQuery<TData = unknown, TResult = TData>(
  endpoint: string,
  queryKey: unknown[],
  options?: Omit<
    UseQueryOptions<ApiResponse<TData>, AxiosError<ApiError>, TResult>,
    'queryKey' | 'queryFn'
  >,
) {
  const { axiosClient } = useAxios();

  const queryOptions: UseQueryOptions<
    ApiResponse<TData>,
    AxiosError<ApiError>,
    TResult
  > = {
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await axiosClient.get<ApiResponse<TData>>(endpoint, { signal });
      return response.data;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  };

  // Default select: extract response.data, treating null as undefined
  // so that destructuring defaults (e.g. `const { data: items = [] }`) work correctly.
  const defaultSelect = (response: ApiResponse<TData>) =>
    (response.data ?? undefined) as unknown as TResult;

  if (options) {
    if (!options.select) {
      queryOptions.select = defaultSelect;
    }
    Object.assign(queryOptions, options);
  } else {
    queryOptions.select = defaultSelect;
  }

  return useReactQuery<ApiResponse<TData>, AxiosError<ApiError>, TResult>(
    queryOptions,
  );
}

/**
 * Custom hook for data mutation using React Query and our API client
 *
 * @param endpoint - API endpoint to send data to
 * @param options - Additional React Query options
 * @returns React Query mutation result
 */
export function useMutation<TData = unknown, TVariables = unknown>(
  endpoint: string,
  options?: Omit<
    UseMutationOptions<ApiResponse<TData>, AxiosError<ApiError>, TVariables>,
    'mutationFn'
  >,
) {
  const { axiosClient } = useAxios();

  const mutationOptions: UseMutationOptions<
    ApiResponse<TData>,
    AxiosError<ApiError>,
    TVariables
  > = {
    mutationFn: async (variables) => {
      const response = await axiosClient.post<ApiResponse<TData>>(
        endpoint,
        variables,
      );
      return response.data;
    },
  };

  if (options) {
    Object.assign(mutationOptions, options);
  }

  return useReactMutation<ApiResponse<TData>, AxiosError<ApiError>, TVariables>(
    mutationOptions,
  );
}

/**
 * Custom hook for data updating using React Query and our API client
 *
 * @param endpoint - API endpoint to update data
 * @param options - Additional React Query options
 * @returns React Query mutation result
 */
export function useUpdate<TData = unknown, TVariables = unknown>(
  endpoint: string,
  options?: Omit<
    UseMutationOptions<ApiResponse<TData>, AxiosError<ApiError>, TVariables>,
    'mutationFn'
  >,
) {
  const { axiosClient } = useAxios();

  const mutationOptions: UseMutationOptions<
    ApiResponse<TData>,
    AxiosError<ApiError>,
    TVariables
  > = {
    mutationFn: async (variables) => {
      const response = await axiosClient.put<ApiResponse<TData>>(
        endpoint,
        variables,
      );
      return response.data;
    },
  };

  if (options) {
    Object.assign(mutationOptions, options);
  }

  return useReactMutation<ApiResponse<TData>, AxiosError<ApiError>, TVariables>(
    mutationOptions,
  );
}

/**
 * Custom hook for partial updates using React Query and our API client (PATCH)
 *
 * @param endpoint - API endpoint to patch data
 * @param options - Additional React Query options
 * @returns React Query mutation result
 */
export function usePatch<TData = unknown, TVariables = unknown>(
  endpoint: string,
  options?: Omit<
    UseMutationOptions<ApiResponse<TData>, AxiosError<ApiError>, TVariables>,
    'mutationFn'
  >,
) {
  const { axiosClient } = useAxios();

  const mutationOptions: UseMutationOptions<
    ApiResponse<TData>,
    AxiosError<ApiError>,
    TVariables
  > = {
    mutationFn: async (variables) => {
      const response = await axiosClient.patch<ApiResponse<TData>>(
        endpoint,
        variables,
      );
      return response.data;
    },
  };

  if (options) {
    Object.assign(mutationOptions, options);
  }

  return useReactMutation<ApiResponse<TData>, AxiosError<ApiError>, TVariables>(
    mutationOptions,
  );
}

/**
 * Custom hook for data deletion using React Query and our API client
 *
 * @param endpoint - API endpoint to delete data
 * @param options - Additional React Query options
 * @returns React Query mutation result
 */
export function useDelete<TData = unknown>(
  endpoint: string,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<TData>,
      AxiosError<ApiError>,
      string | number
    >,
    'mutationFn'
  >,
) {
  const { axiosClient } = useAxios();

  const mutationOptions: UseMutationOptions<
    ApiResponse<TData>,
    AxiosError<ApiError>,
    string | number
  > = {
    mutationFn: async (id) => {
      const response = await axiosClient.delete<ApiResponse<TData>>(
        `${endpoint}/${id}`,
      );
      return response.data;
    },
  };

  if (options) {
    Object.assign(mutationOptions, options);
  }

  return useReactMutation<
    ApiResponse<TData>,
    AxiosError<ApiError>,
    string | number
  >(mutationOptions);
}
